import type { ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'

import { skipTenantCheck } from '../dev/flags'
import { isMurmurationStaff } from './claims'
import { useAuth } from './use-auth'

/**
 * RouteGuard - protects authenticated routes.
 *
 * Decision matrix:
 *   - MSAL still resolving (`inProgress`)      -> render a quiet loading
 *                                                  status (no flicker).
 *   - Not signed in                            -> redirect to /sign-in
 *                                                  with `returnTo` stashed
 *                                                  in OIDC `state` (see
 *                                                  `use-return-to.ts`).
 *                                                  We do NOT use route
 *                                                  state for this because
 *                                                  it does not survive the
 *                                                  IdP round-trip.
 *   - Signed in but `tnt != suitsme`           -> redirect to /access-denied.
 *   - Signed in but missing `bo.staff` role    -> redirect to /access-denied.
 *   - `requireMurmurationStaff` and the token
 *     carries no Murmuration role              -> redirect to /access-denied.
 *   - Otherwise                                -> render the route.
 *
 * Defence-in-depth: the API Gateway native authoriser + the BFF Lambda
 * already enforce these checks server-side. The SPA-side check guards
 * against config-drift on the Entra app registration (a token issued
 * without `tnt` would otherwise let the staff member see an empty list
 * page before the first API call errors).
 *
 * `requireMurmurationStaff` is the same posture applied to the `Platform`
 * group - Murmuration's own control plane, which the MUR page "Information
 * Architecture and Navigation" says a credit union user "must never see".
 * The sidebar already hides those entries; this stops the URL being typed.
 * **Neither is the control.** That page is explicit that gating is
 * server-side, and both of these are presentation over a boundary the
 * server has to hold on its own.
 */
export interface RouteGuardProps {
  readonly children: ReactNode
  /**
   * Restrict to Murmuration staff. No Entra token issues those roles
   * today, so a guarded route is unreachable for everyone - which is the
   * correct default for a control plane.
   */
  readonly requireMurmurationStaff?: boolean
}

export function RouteGuard({
  children,
  requireMurmurationStaff = false,
}: RouteGuardProps): JSX.Element {
  const { signedIn, inProgress, claims, authorised } = useAuth()
  const location = useLocation()

  if (inProgress) {
    return (
      <div
        role="status"
        aria-live="polite"
        className="flex h-full items-center justify-center text-text-muted"
      >
        <span className="sr-only">Checking sign-in</span>
      </div>
    )
  }

  if (!signedIn) {
    const returnTo = `${location.pathname}${location.search}`
    return <Navigate to="/sign-in" replace state={{ returnTo }} />
  }

  // Signed in but unauthorised (wrong tenant or missing role).
  if (!authorised) {
    // Capture the reason in route state so /access-denied can describe it.
    // With the dev tenant bypass on, a tnt mismatch is not the blocker -
    // the only remaining reason to be unauthorised is the missing role.
    const reason =
      !skipTenantCheck && claims?.tnt !== 'suitsme' ? 'tenant_mismatch' : 'role_missing'
    return <Navigate to="/access-denied" replace state={{ reason }} />
  }

  // Staff, but not Murmuration staff, on a control-plane route.
  if (requireMurmurationStaff && !isMurmurationStaff(claims)) {
    return <Navigate to="/access-denied" replace state={{ reason: 'role_missing' }} />
  }

  return <>{children}</>
}
